/** @jsx jsx */
import * as React from "react"
import { ModalComponent } from "./ModalComponent";
import {
    AppConstants,
    GCMBannerConfig,
    ModalSize,
    NotificationPermissionStatus,
    ReferenceEntities, toastNotificationAppearance
} from "../utils/AppConstants";
import { css, jsx } from "@emotion/core"
import { InformationModalStyles } from "../styles/components/InformationModal";
import { AlbusUrlConfigs } from "../api/AlbusUrlConfigs";
import { PushNotification } from "../PushNotification";
import { ApiManager } from "../api/ApiManager";
import { IFeatureConfigResponse } from "../models/IFeatureConfig";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import BrowserUtils from "../utils/BrowserUtils";
import { default as AppUtils, EpochConstants } from "../utils/AppUtils";
import { EventNames } from "../utils/EventTrackerConstants";

interface NotificationPermissionModalProps {
    isAdmin: boolean,
    doctorId: number
}

interface IGCMPermissionLocalStorage {
    timeStamp: number,
    status: boolean,
    bannerShowCount: number
}

const NotificationPermissionModal = (props: NotificationPermissionModalProps) => {
    const [isOpen, toggleModal] = React.useState(false)
    const [isLoading, toggleLoader] = React.useState(false)

    React.useEffect(() => {
        if (BrowserUtils.isChrome() && !BrowserUtils.isChromeOnIos() && !props.isAdmin) {
            const params = {
                [ReferenceEntities.DOCTOR]: props.doctorId
            }
            ApiManager.getOllivanderServiceApis().fetchFeatueConfigInfo({
                configName: "OrionEnableGcmNotification",
                searchParams: [
                    {
                        referenceEntity: ReferenceEntities.DOCTOR,
                        value: params[ReferenceEntities.DOCTOR]
                    }
                ]
            }).then(async (response: IFeatureConfigResponse) => {
                const showModalResponse = await shouldShowModal(response)
                if (showModalResponse) {
                    toggleModal(true)
                }
            })
        }
    }, [])

    const shouldShowModal = async (configResponse: IFeatureConfigResponse): Promise<boolean> => {
        if (configResponse && configResponse.configValue === "TRUE" && "Notification" in window) {
            let gcmStatus = LocalStorageUtils.getItems(AppConstants.GCM_STATUS_LOCAL_STORAGE_KEY)
            try {
                const swReg = await navigator.serviceWorker.getRegistration("/js/service-worker.js")
                const hasGCMWorker = swReg && swReg.active && swReg.active.scriptURL && swReg.active.scriptURL.includes("/js/service-worker.js")
                if (!hasGCMWorker && Notification.permission === NotificationPermissionStatus.granted) {
                    const pnBlockUrl: string = AlbusUrlConfigs.getBlockPushSubscriptionUrl(props.doctorId);
                    await PushNotification.failedSubscriptionCallBack(pnBlockUrl)
                    return true
                }
            } catch (err) {
                console.log({ err })
            }
            if (Notification.permission === NotificationPermissionStatus.denied) {
                const pnBlockUrl: string = AlbusUrlConfigs.getBlockPushSubscriptionUrl(props.doctorId);
                PushNotification.failedSubscriptionCallBack(pnBlockUrl)
                return false
            } else if (!AppUtils.isDefined(gcmStatus)) {
                return (Notification.permission === NotificationPermissionStatus.default)
            } else {
                console.log({ timestamp: Date.now() - gcmStatus.timeStamp })
                const isBufferExceeded = (gcmStatus?.timeStamp && ((Date.now() - gcmStatus.timeStamp) > (GCMBannerConfig.numOfDays * EpochConstants.EPOCH_IN_AN_DAY)))
                const exceededMaxShows = gcmStatus?.bannerShowCount > GCMBannerConfig.maxNumOfTimesBannerShown
                return (Notification.permission === NotificationPermissionStatus.default) && isBufferExceeded && !exceededMaxShows
            }
        } else {
            return false
        }
    }

    const setGcmDeniedStatus = () => {
        let gcmStatus = LocalStorageUtils.getItems(AppConstants.GCM_STATUS_LOCAL_STORAGE_KEY)
        let bannerShowCount: number = 1
        if (AppUtils.isDefined(gcmStatus)) {
            bannerShowCount = gcmStatus.bannerShowCount + 1
        }
        const gcmLocalStorageObj: IGCMPermissionLocalStorage = { timeStamp: Date.now(), status: false, bannerShowCount }
        LocalStorageUtils.setItem(AppConstants.GCM_STATUS_LOCAL_STORAGE_KEY, gcmLocalStorageObj)
    }

    const initNotification = () => {
        LocalStorageUtils.setClickEvents({ section: EventNames.Chrome_Notification, widget: EventNames.Chrome_Notification, field: EventNames.Chrome_Notification, value: "Proceed" })
        const pnSaveUrl: string = AlbusUrlConfigs.getsavePushSubscriptionUrl(props.doctorId);
        const pnBlockUrl: string = AlbusUrlConfigs.getBlockPushSubscriptionUrl(props.doctorId);
        toggleLoader(true)
        PushNotification.init(pnSaveUrl, pnBlockUrl, Notification.permission).then(() => {
            LocalStorageUtils.remove([AppConstants.GCM_STATUS_LOCAL_STORAGE_KEY])
            AppUtils.triggerToastNotification("You are now subscribed to notifications!", toastNotificationAppearance.success)
        }).catch((error: any) => {
            console.log(error)
            if ("Notification" in window && Notification.permission === NotificationPermissionStatus.denied) {
                LocalStorageUtils.remove([AppConstants.GCM_STATUS_LOCAL_STORAGE_KEY])
            } else {
                setGcmDeniedStatus()
            }
        }).finally(() => {
            toggleLoader(false)
            toggleModal(false)
        });
    }

    const handleDismiss = () => {
        LocalStorageUtils.setClickEvents({ section: EventNames.Chrome_Notification, widget: EventNames.Chrome_Notification, field: EventNames.Chrome_Notification, value: "Deny" })
        setGcmDeniedStatus()
        toggleModal(false)
    }

    if (!isOpen) return null
    return (
        <ModalComponent modalSize={""} css={InformationModalStyles.modal}>
            <div css={InformationModalStyles.container}>
                {/* <div css={InformationModalStyles.imgContainer}>
                    <img src={`/images/notification-chrome.png`} css={InformationModalStyles.image} />
                </div> */}
                <div css={InformationModalStyles.textBodyContainer}>
                    <div css={InformationModalStyles.textHeader}>
                        <img src={`/images/notification-chrome.png`} css={InformationModalStyles.image} />
                        <span>Send Notifications?</span>
                    </div>
                    <div css={InformationModalStyles.textBody}>Allowing chrome notifications will have the following benefits</div>
                    <ul css={InformationModalStyles.list}>
                        <li css={InformationModalStyles.listItems}><div css={InformationModalStyles.bullet}></div>Get notified when patients send you messages on chat</li>
                        <li css={InformationModalStyles.listItems}><div css={InformationModalStyles.bullet}></div>Stay informed about appointment bookings and cancellations</li>
                        <li css={InformationModalStyles.listItems}><div css={InformationModalStyles.bullet}></div>Receive important communications and reminders</li>
                    </ul>
                    <div className={"cta-container"} css={InformationModalStyles.ctaContainer}>
                        {!isLoading && <input css={InformationModalStyles.primaryCta} type="button" value="Allow" onClick={initNotification} />}
                        {!isLoading && <span css={InformationModalStyles.secondaryCta} onClick={handleDismiss}>Dismiss</span>}
                        {isLoading && <span><img src={"/images/loader/eclipse-loader.gif"} css={InformationModalStyles.loader} /></span>}
                    </div>
                </div>
            </div>
        </ModalComponent>
    )
}

export default NotificationPermissionModal